import { useCallback, useMemo, useState } from 'react';

import { useSelectionContext } from '../../context/SelectionContext.tsx';
import { useLocale } from '../../context/LocaleContext';

import styles from './gwp-card.module.css';

/**
 * Custom hook for GwpCard logic and className selection.
 * @param {Object} props - Props passed to the card component.
 * @returns {Object} View model for the card component.
 */
export function useViewModel(props) {
  const {
    id,
    slotId,
    imageSrc,
    imageAlt,
    title,
    description,
    availableForSale,
    compareAtPrice,
    secondaryOptionsName = 'Select option',
    isSelected = false,
    secondaryOptions: secondaryOptionsRaw,
    secondaryOptionsLabel,
    onSecondarySelect,
    price,
    disableSecondaryOptions,
    badge,
    helpPage,
    variants: componentGroupVariants = [],
    onClick
  } = props;

  const { selections, updateSelections } = useSelectionContext();
  const { formatPrice } = useLocale();
  const [modalOpen, setModalOpen] = useState(false);

  const secondaryOptions = useMemo(
    () => (disableSecondaryOptions ? [] : secondaryOptionsRaw),
    [disableSecondaryOptions, secondaryOptionsRaw]
  );

  // Calculate the final price after applying price modifier.
  const originalPrice = useMemo(() => {
    if (!price || typeof price.amount === 'undefined' || price.amount === null) return null;
    return { amount: Number(price.amount) };
  }, [price]);

  const compareAtPriceObj = useMemo(() => {
    if (!compareAtPrice || typeof compareAtPrice === 'undefined' || compareAtPrice === null) return null;
    return { amount: Number(compareAtPrice) };
  }, [compareAtPrice]);

  const hasSecondaryOptions = Boolean(secondaryOptions && secondaryOptions.length > 1);

  const currentSelectionForSlot = selections?.slots?.find?.((s) => s.id === slotId)?.selected || '';

  // Item label when grouped or not.
  const itemLabel = useMemo(() => {
    if (hasSecondaryOptions && secondaryOptionsName) return secondaryOptionsName;
    return title;
  }, [hasSecondaryOptions, secondaryOptionsName, title]);

  const openModal = useCallback(() => setModalOpen(true), []);

  const handleSecondarySelect = useCallback(
    ({ optionId }) => {
      updateSelections({ slotId, itemSelected: optionId });
      setModalOpen(false);
    },
    [slotId, updateSelections]
  );

  const selectedSecondary = useMemo(
    () => secondaryOptions?.find?.((opt) => opt.id === currentSelectionForSlot),
    [secondaryOptions, currentSelectionForSlot]
  );

  const selectedSecondaryLabel = useMemo(() => {
    if (!selectedSecondary) return secondaryOptionsLabel || 'Choose option';
    return selectedSecondary?.title || secondaryOptionsLabel || 'Choose option';
  }, [selectedSecondary, secondaryOptionsLabel]);

  const handleSelection = useCallback(() => {
    if (!hasSecondaryOptions) {
      updateSelections({ slotId, itemSelected: id });
    } else {
      setModalOpen(true);
    }
  }, [hasSecondaryOptions, id, slotId, updateSelections]);

  const handleLabelClick = useCallback(
    (e) => {
      if (hasSecondaryOptions) {
        e.preventDefault();
        setModalOpen(true);
      }
    },
    [hasSecondaryOptions]
  );

  // Compute summary compare-at for the card from secondary options.
  const { cardCompareAtAmount, cardCompareAtPrefix } = useMemo(() => {
    const amounts = (secondaryOptionsRaw || [])
      .map((opt) => Number(opt?.compareAtPrice?.amount))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (amounts.length === 0) return { cardCompareAtAmount: null, cardCompareAtPrefix: '' };
    const min = Math.min(...amounts);
    const max = Math.max(...amounts);
    if (min === max) return { cardCompareAtAmount: max, cardCompareAtPrefix: 'Worth' };
    return { cardCompareAtAmount: max, cardCompareAtPrefix: 'Worth up to' };
  }, [secondaryOptionsRaw]);

  const priceDisplay = useMemo(() => ({ amount: originalPrice?.amount ?? null }), [originalPrice]);

  return {
    id,
    imageSrc,
    imageAlt,
    title,
    description,
    availableForSale,
    isSelected,
    secondaryOptionsName,
    secondaryOptions,
    secondaryOptionsLabel,
    onSecondarySelect,
    openModal,
    handleSecondarySelect,
    price,
    originalPrice,
    compareAtPriceObj,
    selectedSecondaryLabel,
    modalOpen,
    setModalOpen,
    hasSecondaryOptions,
    currentSelectionForSlot,
    itemLabel,
    updateSelections,
    slotId,
    handleSelection,
    formatPrice,
    handleLabelClick,
    componentGroupVariants,
    badge,
    helpPage,
    helperModalOpen: modalOpen,
    setHelperModalOpen: setModalOpen,
    cardInnerWrapperClassName: styles['personalisation__card-inner-wrapper'],
    cardInnerClassName: styles['personalisation__card-inner'],
    cardCompareAtAmount,
    cardCompareAtPrefix,
    onClick
  };
}
